import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { campaignKeys, useResendOutreachMessages } from '@discovr/features';
import type { OutreachOperation } from '@discovr/types';
import { failedTotal } from './operationOutcomes';
import { retrySummary, retryToastKind } from './retryLabels';
import { entryActionErrorCopy } from './sendLabels';

export interface OutreachRetry {
  /** The resend_messages operation on screen, running or finished with failures. */
  operationId: string | null;
  hasFailures: boolean;
  busy: boolean;
  retry: (messageIds: number[]) => Promise<void>;
  onDone: (operation: OutreachOperation) => void;
  onError: () => void;
  dismiss: () => void;
}

/** U6's Retry: one resend_messages operation at a time; one that finishes
 *  with failures stays on screen until dismissed. */
export function useOutreachRetry(campaignId: string): OutreachRetry {
  const queryClient = useQueryClient();
  const resendMany = useResendOutreachMessages(campaignId);
  const [operationId, setOperationId] = useState<string | null>(null);
  const [hasFailures, setHasFailures] = useState(false);
  const busy = resendMany.isPending || (operationId !== null && !hasFailures);
  // A ref, not state: a second click can land before the first one's re-render.
  const startingRef = useRef(false);
  const runningRef = useRef(false);

  const retry = async (messageIds: number[]) => {
    if (messageIds.length === 0 || busy || startingRef.current || runningRef.current) return;
    startingRef.current = true;
    try {
      const { operationId: id } = await resendMany.mutateAsync({ messageIds });
      runningRef.current = true;
      setHasFailures(false);
      setOperationId(id);
    } catch (e) {
      toast.error(entryActionErrorCopy(e));
    } finally {
      startingRef.current = false;
    }
  };

  const onDone = (operation: OutreachOperation) => {
    runningRef.current = false;
    if (failedTotal(operation) > 0) setHasFailures(true);
    else setOperationId(null);
    toast[retryToastKind(operation)](retrySummary(operation));
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachRowsAll(campaignId) });
    queryClient.invalidateQueries({ queryKey: campaignKeys.outreachCounts(campaignId) });
  };

  const onError = () => {
    runningRef.current = false;
    setOperationId(null);
    toast.error("Couldn't read this batch's progress. Try again.");
  };

  const dismiss = () => {
    setOperationId(null);
    setHasFailures(false);
  };

  return { operationId, hasFailures, busy, retry, onDone, onError, dismiss };
}
